import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ConfirmModal, CustomSelect, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, TableSkeleton, RowActions, RowAction,
  ListEmptyRow, ListPagination, MoreMenu, StatusDot, btnPrimary,
} from '@redpanda/ui';
import { isAdmin, toIntlLocale, useRefetchOnFocus, useRememberedState } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import FiscalYearDialog from '../components/FiscalYearDialog';
import PeriodClosingDialog from '../components/PeriodClosingDialog';
import PeriodClosingDetailDialog from '../components/PeriodClosingDetailDialog';
import {
  AccountingPeriod, FiscalYear, PeriodClosing, formatPeriodDate, formatPeriodFullDate, useFiscalYearStoreBase, usePeriodClosingStoreBase,
} from '../stores/periodStore';
import { deleteFiscalYear, reopenPeriod } from '../services/api';
import { closingTone, toneOf } from '../utils/statusTone';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';

const TABS = ['periods', 'closings'] as const;
type Tab = typeof TABS[number];

/**
 * 期間管理：選會計年度 → 每個月一列，開放的期間可結帳、已結帳的可重新開啟；另一個分頁是結帳記錄
 * 原本一頁三張長表（年度、全部期間、結帳記錄）、頁首三色按鈕（含開發用的「清理結算記錄」：會刪掉所有組織的結帳記錄）
 */
const PeriodManagementPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = toIntlLocale(i18n.language);
  const { user } = useContext(UserContext)!;
  const admin = isAdmin(user?.role);

  const yearStore = useFiscalYearStoreBase();
  const closingStore = usePeriodClosingStoreBase();
  const [tab, setTab] = useRememberedState<Tab>('accounting-ui:periods.tab', 'periods', TABS);
  const [selectedYearId, setSelectedYearId] = useState('');
  const [yearDialog, setYearDialog] = useState<FiscalYear | null | undefined>(undefined);
  const [closingPeriod, setClosingPeriod] = useState<AccountingPeriod | null>(null);
  const [reopenTarget, setReopenTarget] = useState<AccountingPeriod | null>(null);
  const [reopening, setReopening] = useState(false);
  const [detailClosing, setDetailClosing] = useState<PeriodClosing | null>(null);
  const [deleteYear, setDeleteYear] = useState<FiscalYear | null>(null);
  const [deletingYear, setDeletingYear] = useState(false);

  const years = yearStore.items;
  useEffect(() => {
    yearStore.fetchItems(1);
    closingStore.fetchItems(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshAll = () => {
    useFiscalYearStoreBase.getState().refreshData();
    usePeriodClosingStoreBase.getState().refreshData();
  };
  useRefetchOnFocus(refreshAll);
  useDataChanged('periods', refreshAll);

  // 預設選「包含今天的年度」，沒有就選最新的
  const todayIso = new Date().toISOString();
  const currentYear = years.find(y => y.startDate <= todayIso && y.endDate >= todayIso) ?? years[0];
  const selectedYear = years.find(y => y.id === selectedYearId) ?? currentYear;
  const periods = selectedYear?.periods ?? [];

  const handleReopen = async () => {
    if (!reopenTarget) return;
    setReopening(true);
    try {
      await reopenPeriod(reopenTarget.id);
      toast.success(t('periods.toast.reopenPeriodSuccess', { name: reopenTarget.name }));
      refreshAll();
      notifyDataChanged('periods');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'periods.toast.reopenFailed'));
    } finally {
      setReopening(false);
      setReopenTarget(null);
    }
  };

  const handleDeleteYear = async () => {
    if (!deleteYear) return;
    setDeletingYear(true);
    try {
      await deleteFiscalYear(deleteYear.id);
      toast.success(t('common.messages.deleteSuccess'));
      setSelectedYearId('');
      refreshAll();
      notifyDataChanged('periods');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'common.messages.deleteFailed'));
    } finally {
      setDeletingYear(false);
      setDeleteYear(null);
    }
  };

  const range = (start: string, end: string) => `${formatPeriodDate(start, locale)} – ${formatPeriodDate(end, locale)}`;
  const loading = yearStore.isLoading && years.length === 0;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('periods.title')}</h1>
          <div className="flex items-center space-x-3 whitespace-nowrap">
            {admin && (
              <button type="button" onClick={() => setYearDialog(null)} className={btnPrimary}>{t('periods.addFiscalYear')}</button>
            )}
            {admin && selectedYear && (
              <MoreMenu
                variant="icon"
                label={t('accounts.list.moreActions')}
                items={[
                  { key: 'edit', label: t('periods.editFiscalYear', { name: selectedYear.name }), onClick: () => setYearDialog(selectedYear) },
                  { key: 'delete', label: t('periods.deleteFiscalYear', { name: selectedYear.name }), tone: 'danger', onClick: () => setDeleteYear(selectedYear) },
                ]}
              />
            )}
          </div>
        </div>
      </div>

      <nav className="mb-4 flex gap-6 border-b border-line overflow-x-auto scrollbar-hide" aria-label={t('periods.title')}>
        {TABS.map(key => (
          <button
            key={key}
            type="button"
            aria-current={tab === key ? 'page' : undefined}
            onClick={() => setTab(key)}
            className={`-mb-px h-10 whitespace-nowrap border-b-2 text-sm font-medium transition-colors ${
              tab === key ? 'border-accent text-accent' : 'border-transparent text-fg-secondary hover:text-fg'
            }`}
          >
            {t(`periods.tabs.${key}`)}
          </button>
        ))}
      </nav>

      {tab === 'periods' && (
        loading ? (
          <TableSkeleton rows={12} columns={4} showHeader={true} />
        ) : !selectedYear ? (
          <div className="bg-surface rounded-lg border border-line px-5 py-12 text-center">
            <p className="text-sm font-medium text-fg">{t('periods.noFiscalYears')}</p>
            <p className="mt-1 text-sm text-fg-secondary">{t('periods.noFiscalYearsHint')}</p>
            {admin && <button type="button" onClick={() => setYearDialog(null)} className={`${btnPrimary} mt-4`}>{t('periods.addFiscalYear')}</button>}
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
              <div className="md:w-48">
                <CustomSelect
                  value={selectedYear.id}
                  onChange={setSelectedYearId}
                  options={years.map(year => ({ value: year.id, label: year.name }))}
                />
              </div>
              <p className="text-sm text-fg-secondary tabular-nums md:ml-auto">
                {formatPeriodFullDate(selectedYear.startDate, locale)} – {formatPeriodFullDate(selectedYear.endDate, locale)}
                <span className="mx-2 text-fg-muted">·</span>
                {t('periods.closedCount', { closed: periods.filter(p => p.isClosed).length, total: periods.length })}
              </p>
            </div>
            <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
              <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <Table style={{ tableLayout: 'fixed', minWidth: '640px' }}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('periods.table.name')}</TableHead>
                      <TableHead numeric className="w-44">{t('periods.table.range')}</TableHead>
                      <TableHead numeric className="w-28">{t('periods.table.entries')}</TableHead>
                      <TableHead className="w-28">{t('periods.table.status')}</TableHead>
                      <TableHead className="w-28">{t('periods.table.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {periods.map((period, index) => (
                      <TableRow key={period.id}>
                        <TableCell className="font-medium text-fg">{period.name}</TableCell>
                        <TableCell numeric>{range(period.startDate, period.endDate)}</TableCell>
                        <TableCell numeric>{period._count?.journalEntries ?? <span className="text-fg-muted">—</span>}</TableCell>
                        <TableCell>
                          <StatusDot tone={period.isClosed ? 'success' : 'neutral'} label={period.isClosed ? t('periods.status.closed') : t('periods.status.open')} />
                        </TableCell>
                        <TableCell>
                          {admin && (
                            <RowActions>
                              {period.isClosed ? (
                                <RowAction onClick={() => setReopenTarget(period)}>{t('periods.actions.reopen')}</RowAction>
                              ) : (
                                <RowAction onClick={() => setClosingPeriod(period)}>{t('periods.actions.close')}</RowAction>
                              )}
                            </RowActions>
                          )}
                          {closingPeriod?.id === period.id && (
                            <PeriodClosingDialog
                              period={period}
                              isLastPeriod={index === periods.length - 1}
                              onClose={() => setClosingPeriod(null)}
                              onClosed={() => { refreshAll(); notifyDataChanged('periods'); }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <p className="mt-3 text-[13px] text-fg-secondary">{t('periods.closingHint')}</p>
          </>
        )
      )}

      {tab === 'closings' && (
        <>
          <ListPagination pagination={closingStore.pagination} onPageChange={page => closingStore.fetchItems(page)} isLoading={closingStore.isPageLoading} className="mb-3" />
          {closingStore.isLoading && closingStore.items.length === 0 ? (
            <TableSkeleton rows={6} columns={6} showHeader={true} />
          ) : (
            <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
              <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <Table style={{ tableLayout: 'fixed', minWidth: '760px' }}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-44">{t('periods.closings.period')}</TableHead>
                      <TableHead className="w-24">{t('periods.closings.type')}</TableHead>
                      <TableHead className="w-28">{t('periods.closings.status')}</TableHead>
                      <TableHead numeric className="w-40">{t('periods.closings.completedAt')}</TableHead>
                      <TableHead>{t('periods.closingDialog.notes')}</TableHead>
                      <TableHead className="w-20">{t('periods.table.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {closingStore.items.length === 0 ? (
                      <ListEmptyRow colSpan={6} filtered={false} title={t('periods.closings.empty')} hint={t('periods.closingHint')} />
                    ) : (
                      closingStore.items.map(closing => (
                        <TableRow key={closing.id}>
                          <TableCell className="truncate font-medium text-fg">
                            {closing.period?.name ?? '—'}
                            {closing.period?.fiscalYear?.name && <span className="block text-[13px] font-normal text-fg-secondary">{closing.period.fiscalYear.name}</span>}
                          </TableCell>
                          <TableCell>{t(`periods.closingTypes.${closing.type}`)}</TableCell>
                          <TableCell><StatusDot tone={toneOf(closingTone, closing.status)} label={t(`periods.closingStatus.${closing.status}`)} /></TableCell>
                          <TableCell numeric>
                            {closing.completedAt ? new Date(closing.completedAt).toLocaleDateString(locale, { month: 'numeric', day: 'numeric', year: 'numeric' }) : <span className="text-fg-muted">—</span>}
                          </TableCell>
                          <TableCell className="truncate" title={closing.notes || undefined}>{closing.notes || <span className="text-fg-muted">—</span>}</TableCell>
                          <TableCell>
                            <RowActions>
                              <RowAction onClick={() => setDetailClosing(closing)}>{t('common.actions.view')}</RowAction>
                            </RowActions>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <ListPagination pagination={closingStore.pagination} onPageChange={page => closingStore.fetchItems(page)} isLoading={closingStore.isPageLoading} className="mt-6" />
        </>
      )}

      <ConfirmModal
        isOpen={!!reopenTarget}
        onClose={() => setReopenTarget(null)}
        onConfirm={handleReopen}
        title={t('periods.reopenModal.title', { name: reopenTarget?.name ?? '' })}
        message={t('periods.reopenModal.message')}
        confirmText={t('periods.actions.reopen')}
        cancelText={t('common.actions.cancel')}
        isLoading={reopening}
        loadingText={t('common.status.processing')}
        t={t}
      />

      <ConfirmModal
        isOpen={!!deleteYear}
        onClose={() => setDeleteYear(null)}
        onConfirm={handleDeleteYear}
        title={t('periods.deleteFiscalYear', { name: deleteYear?.name ?? '' })}
        message={t('periods.deleteFiscalYearMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={deletingYear}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      {yearDialog !== undefined && (
        <FiscalYearDialog
          fiscalYear={yearDialog ?? undefined}
          defaultYear={years.length > 0 ? Math.max(...years.map(y => new Date(y.endDate).getUTCFullYear())) + 1 : new Date().getFullYear()}
          onClose={() => setYearDialog(undefined)}
          onSaved={saved => setSelectedYearId(saved.id)}
        />
      )}
      {detailClosing && <PeriodClosingDetailDialog closing={detailClosing} userRole={user?.role} onClose={() => setDetailClosing(null)} />}
    </div>
  );
};

export default PeriodManagementPage;
